import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { t } from 'i18next'

import EmptyPage from '~/components/empty-page'
import { Filters } from '~/components/Filters'
import HeaderPages from '~/components/HeaderPages'
import PageTitle from '~/components/PageTitle'
import { useAppInfo } from '~/hooks/useAppInfo'
import { useAppSelector } from '~/store/hooks'

import RetailMediaExternalReport from '../../../components/Report'
import Tabs from '../../../components/Tabs'
import { advertiserPublisherFilters } from '../../../store/common/filters/advertiserPublisher'

const RetailMediaReportV2 = () => {
  const { publisher, advertiser } = useAppSelector(
    state => state.filters.retailMedia
  )

  const { accountType } = useAppSelector(state => state.signin)

  const headerFilters = useMemo(
    () => ({
      genericFilters: [advertiserPublisherFilters[accountType]]
    }),
    [accountType]
  )

  const { isWhiteLabel } = useAppInfo()

  /**
   * Params
   */
  type ExternalDashboardKeys =
    | 'ads'
    | 'overview'
    | 'hour_overview'
    | 'advertisers'
    | 'advertisers_state'
    | 'opportunity_cost'
    | 'daily_consolidation'
    | 'network_subpublisher_performance'

  const location = useLocation()

  const getKeyFromURL = useMemo(() => {
    const path = location.pathname
    const parts = path.split('/')

    return parts[parts.length - 1] as ExternalDashboardKeys
  }, [location.pathname])

  type DashboardNames = {
    [key in ExternalDashboardKeys]: ExternalDashboardNames
  }

  type DashboardTitles = {
    [key in ExternalDashboardKeys]: string
  }

  const dashboardNames: DashboardNames = {
    ads: 'campaign_resume',
    overview: 'management_report',
    hour_overview: 'management_report_by_hour',
    advertisers: 'advertisers',
    advertisers_state: 'advertisers_state',
    opportunity_cost: 'opportunity_cost',
    daily_consolidation: 'daily_consolidation',
    network_subpublisher_performance: 'network_subpublisher_performance'
  }

  const dashboardTitles: DashboardTitles = {
    ads: t('rm:Ads'),
    overview: t('rm:reports.management.title'),
    hour_overview: t('rm:reports.hourlyManagement.title'),
    advertisers: t('rm:reports.advertisers.tab.title'),
    advertisers_state: t('rm:reports.advertisersState.tab.title'),
    opportunity_cost: t('rm:reports.opportunityCost.tab.title'),
    daily_consolidation: t('rm:reports.dailyConsolidation.tab.title'),
    network_subpublisher_performance: t(
      'rm:reports.networkSubpublisherPerformance.tab.title'
    )
  }

  const customFilters = useMemo(
    () => ({
      ads: {
        advertiser_id: advertiser?.value,
        publisher_id: publisher?.value
      },
      overview: null,
      hour_overview: null,
      advertisers: {
        advertiser_id: advertiser?.value
      },
      advertisers_state: {
        advertiser_id: advertiser?.value
      },
      opportunity_cost: {
        advertiser_id: advertiser?.value
      },
      daily_consolidation: null,
      network_subpublisher_performance: null
    }),
    [publisher, advertiser]
  )

  const tabs = useMemo(
    () =>
      getKeyFromURL === 'ads'
        ? []
        : [
            {
              to: `/retail-media/report/overview`,
              label: t('rm:reports.management.tab.title')
            },
            {
              to: `/retail-media/report/hour_overview`,
              label: t('rm:reports.hourlyManagement.tab.title')
            },
            {
              to: `/retail-media/report/advertisers`,
              label: t('rm:reports.advertisers.tab.title')
            },
            {
              to: `/retail-media/report/advertisers_state`,
              label: t('rm:reports.advertisersState.tab.title')
            },
            {
              to: `/retail-media/report/opportunity_cost`,
              label: t('rm:reports.opportunityCost.tab.title')
            },
            {
              to: `/retail-media/report/daily_consolidation`,
              label: t('rm:reports.dailyConsolidation.tab.title')
            },
            {
              to: `/retail-media/report/network_subpublisher_performance`,
              label: t('rm:reports.networkSubpublisherPerformance.tab.title')
            }
          ],
    [getKeyFromURL]
  )

  const needToFillAdvertiser =
    getKeyFromURL === 'advertisers_state' && !advertiser?.value

  return (
    <>
      <PageTitle
        title={`Relatório - ${dashboardTitles?.[getKeyFromURL]} - Retail media`}
      />

      <HeaderPages
        title={`Retail Media - Relatórios > ${dashboardTitles?.[getKeyFromURL]}`}
        injectDatepicker
        className="has-datepicker"
      />

      <Tabs tabs={tabs} />

      {customFilters?.[getKeyFromURL] && <Filters {...headerFilters} />}

      {!needToFillAdvertiser && (
        <RetailMediaExternalReport
          useDate
          customFilters={customFilters?.[getKeyFromURL]}
          dashboardName={dashboardNames?.[getKeyFromURL]}
        />
      )}

      {needToFillAdvertiser && (
        <EmptyPage
          title="Selecione um anunciante"
          subtitle="Para começar precisamos definir um anunciante."
          svgPath="photos/orders-empty"
        />
      )}
    </>
  )
}

export default RetailMediaReportV2
